import {
  CalculatorResolve,
  Patient,
  PatientInfo,
  PatientList,
  PatientPostResult,
  PatientPostUploadResult,
  PatientResolve,
  PatientVM,
  PNBlob,
  ProgressNote,
  ProgressNoteSelected,
  RecordDiagnostic,
  Role,
  UpdateBillingSemester,
  UpdatePatientStatus,
  PatientsLoadedResolve
} from '@/models'
import axios, { AxiosResponse } from 'axios'
import { getUserRole } from '@/components/admin/users/users.service'
const API_URL = 'Patients'

export const getPatientsAll = async (
  onlyActive = false
): Promise<AxiosResponse<Patient[]>> => {
  let doctorId: string | null = null
  if (getUserRole() == Role.Staff) {
    doctorId = localStorage.getItem('selectedDoctor')
  }
  // let url = `?pageNumber=${pageNumber}&pageSize=${pageSize}&InsuranceName=${insuranceName}&SearchQuery=${searchQuery}`
  let url = `/GetAll`
  if (onlyActive) {
    url += `?Status=${onlyActive}`
  }
  if (doctorId && onlyActive) {
    url += `&doctorId=${doctorId}`
  } else if (doctorId && !onlyActive) {
    url += `?doctorId=${doctorId}`
  }
  const response = await axios.get<Patient[]>(API_URL + url)
  return response || null
}
export const getPatients = async (
  pageNumber = 1,
  pageSize = 10,
  searchQuery = '',
  _insuranceName = '',
  insurances = '',
  patientStatus = '',
  exportAll = false,
  insuranceIPA = ''
): Promise<AxiosResponse<Patient[]>> => {
  // Role scoping is resolved in the API/SP from the logged-in RMT_UserID.
  // Staff must pass the doctor chosen on DoctorSelection (SP has no Staff branch).
  const params: Record<string, string | number | boolean> = {
    pageNumber,
    pageSize,
    ExportAll: exportAll
  }
  if (searchQuery) params.SearchQuery = searchQuery
  if (insurances) params.insurances = insurances
  if (insuranceIPA) params.InsuranceIPA = insuranceIPA
  if (patientStatus !== '' && patientStatus != null) {
    params.patientStatus = patientStatus
  }
  if (getUserRole() == Role.Staff) {
    const doctorId = localStorage.getItem('selectedDoctor')
    if (doctorId) params.doctorId = doctorId
  }

  const response = await axios.get<Patient[]>(API_URL, { params })
  return response || null
}
export const getPatientsList = async (
  pageNumber = 1,
  pageSize = 10,
  searchQuery = '',
  insuranceName = '',
  insurances = ''
): Promise<AxiosResponse<PatientList[]>> => {
  let doctorId: string | null = null
  if (getUserRole() == Role.Staff) {
    doctorId = localStorage.getItem('selectedDoctor')
  }
  let url =
    `/GetPatientList?pageNumber=${pageNumber}&pageSize=${pageSize}` +
    `&InsuranceName=${insuranceName || ''}` +
    `&SearchQuery=${searchQuery || ''}` +
    `&insurances=${insurances || ''}`

  if (doctorId) {
    url += `&doctorId=${doctorId}`
  }
  const response = await axios.get<PatientList[]>(API_URL + url)
  return response || null
}
export const getPatientByRecord = async (
  recordID: string
): Promise<PatientInfo[]> => {
  const { data } = await axios.get<PatientInfo[]>(
    API_URL + `/GetByRecord?RecordID=${recordID}`
  )
  return data
}
export const getRecordDiagnostics = async (
  pageNumber = 1,
  pageSize = 10,
  recordID: string
): Promise<AxiosResponse<RecordDiagnostic[]>> => {
  const response = await axios.get<RecordDiagnostic[]>(
    API_URL +
      `/GetRecordDiagnostics?pageNumber=${pageNumber}&pageSize=${pageSize}&RecordID=${recordID}`
  )
  return response || null
}

export const getProgressNotes = async (
  pageNumber = 1,
  pageSize = 10,
  recordID: string
): Promise<AxiosResponse<ProgressNote[]>> => {
  const response = await axios.get<ProgressNote[]>(
    API_URL +
      `/GetProgressNotes?pageNumber=${pageNumber}&pageSize=${pageSize}&RecordID=${recordID}`
  )
  return response || null
}
export const getSelectedProgressNote = async (
  progressNoteID: number,
  recordID: string
): Promise<PNBlob> => {
  const { data } = await axios.get<PNBlob>(
    API_URL +
      `/GetProgressNoteById?ProgressNoteID=${progressNoteID}&RecordID=${recordID}`
  )
  return data
}
export const getResolve = async (): Promise<PatientResolve> => {
  const { data } = await axios.get<PatientResolve>(API_URL + `/PatientsResolve`)
  return data
}
export const getCalculatorResolve = async (): Promise<CalculatorResolve> => {
  const { data } = await axios.get<CalculatorResolve>(
    API_URL + `/CalculatorResolve`
  )
  return data
}
export const calculateMasterRaf = async (
  model: UpdateBillingSemester
): Promise<UpdateBillingSemester[]> => {
  const { data } = await axios.post<UpdateBillingSemester[]>(
    API_URL + `/CalculateMasterRaf`,
    model
  )
  return data
}
export const updateBillingSemester = async (
  model: UpdateBillingSemester
): Promise<UpdateBillingSemester[]> => {
  const { data } = await axios.put<UpdateBillingSemester[]>(
    API_URL + `/UpdateBillingSemester`,
    model
  )
  return data
}
export const updateBillingSemesterList = async (
  model: UpdateBillingSemester
): Promise<UpdateBillingSemester[]> => {
  const { data } = await axios.put<UpdateBillingSemester[]>(
    API_URL + `/UpdateBillingSemesterList`,
    model
  )
  return data
}
export const updatePatientStatus = async (
  model: UpdatePatientStatus
): Promise<UpdatePatientStatus[]> => {
  const { data } = await axios.put<UpdatePatientStatus[]>(
    API_URL + `/UpdatePatientStatus`,
    model
  )
  return data
}
export const updatePatient = async (model: PatientVM): Promise<PatientVM> => {
  const { data } = await axios.put<PatientVM>(API_URL + `/UpdatePatient`, model)
  return data
}
export const postPatient = async (
  model: PatientVM
): Promise<PatientPostResult> => {
  const { data } = await axios.post(API_URL, model)
  return data
}
export const uploadFile = async (
  file: FormData
): Promise<PatientPostResult> => {
  const { data } = await axios.post(API_URL + `/Upload`, file)
  return data
}
export const uploaMassivePatientsFile = async (
  file: FormData
): Promise<PatientPostUploadResult> => {
  const { data } = await axios.post(API_URL + `/UploadMassivePatient`, file, {
    timeout: 0
  })
  return data
}

export const getPatientsLoaded = async (
  pageNumber = 1,
  pageSize = 10,
  uploadId = ''
): Promise<AxiosResponse<PatientsLoadedResolve[]>> => {
  const response = await axios.get<PatientsLoadedResolve[]>(
    API_URL +
      `/PatientsLoaded?pageNumber=${pageNumber}&pageSize=${pageSize}&uploadId=${uploadId}`
  )
  return response || null
}
export const syncLoadedPatients = async (uploadId: string): Promise<string> => {
  const { data } = await axios.post(
    `${API_URL}/SyncLoadedPatients?uploadId=${uploadId}`
  )
  return data
}
